import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import { FC, useState, useEffect } from 'react';
import styled from 'styled-components';

interface BackgroundImageAdjustmentModalProps {
	open: boolean;
	backgroundImage: string;
	initialGrayscale?: number;
	initialOpacity?: number;
	onConfirm: (grayscale: number, opacity: number) => void;
	onSkip: () => void;
}

const BackgroundImageAdjustmentModal: FC<BackgroundImageAdjustmentModalProps> = ({ open, backgroundImage, initialGrayscale = 0, initialOpacity = 100, onConfirm, onSkip }) => {
	const [grayscale, setGrayscale] = useState(initialGrayscale);
	const [opacity, setOpacity] = useState(initialOpacity);

	// Reset values when modal opens
	useEffect(() => {
		if (open) {
			setGrayscale(initialGrayscale);
			setOpacity(initialOpacity);
		}
	}, [open, initialGrayscale, initialOpacity]);

	const handleConfirm = () => {
		onConfirm(grayscale, opacity / 100); // Convert opacity to 0-1 range
	};

	const handleGrayscaleChange = (_event: Event, newValue: number | number[]) => {
		setGrayscale(newValue as number);
	};

	const handleOpacityChange = (_event: Event, newValue: number | number[]) => {
		setOpacity(newValue as number);
	};

	return (
		<Modal open={open} aria-labelledby="background-adjustment-title" aria-describedby="background-adjustment-description">
			<Box
				sx={{
					position: 'absolute' as 'absolute',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					width: '90%',
					maxWidth: '400px',
					bgcolor: 'background.paper',
					border: '1px solid #000',
					boxShadow: 24,
					borderRadius: 3,
					p: 3,
				}}
			>
				<Typography id="background-adjustment-title" variant="h6" component="h2" align="center" mb={1}>
					Adjust Background Image
				</Typography>
				<Typography variant="body2" align="center" color="text.secondary" mb={2}>
					Customize the appearance of your background image
				</Typography>
				<Divider variant="middle" light />

				{/* Image Preview */}
				<Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
					<PreviewImage src={backgroundImage} alt="Background preview" $grayscale={grayscale} $opacity={opacity / 100} />
				</Box>

				{/* Grayscale Slider */}
				<Box>
					<Typography variant="body2">Grayscale: {grayscale}%</Typography>
					<Slider value={grayscale} onChange={handleGrayscaleChange} min={0} max={100} step={1} valueLabelDisplay="auto" sx={{ color: 'primary.main' }} />
				</Box>

				{/* Opacity Slider */}
				<Box>
					<Typography variant="body2">Opacity: {opacity}%</Typography>
					<Slider value={opacity} onChange={handleOpacityChange} min={0} max={100} step={1} valueLabelDisplay="auto" sx={{ color: 'primary.main' }} />
				</Box>

				{/* Action Buttons */}
				<Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
					<Button variant="outlined" fullWidth onClick={onSkip}>
						Skip
					</Button>
					<Button variant="contained" fullWidth onClick={handleConfirm}>
						Apply
					</Button>
				</Box>
			</Box>
		</Modal>
	);
};

export default BackgroundImageAdjustmentModal;

const PreviewImage = styled.img<{ $grayscale: number; $opacity: number }>`
	width: 200px;
	height: 200px;
	object-fit: contain;
	border-radius: 8px;
	filter: grayscale(${props => props.$grayscale}%);
	opacity: ${props => props.$opacity};
	transition: filter 0.2s ease, opacity 0.2s ease;
`;
